/**
 * A route's active days, as words and as a small badge.
 *
 * Day indices as the engine stores them: Monday = 0 through Sunday = 6,
 * evaluated in Europe/London. Numbers, not names: `check_active_days` parses the
 * array as integers and treats a parse failure as "every day", so a day name
 * would silently remove the gate rather than error. The server resolves what a
 * link inherits (`days` on a link); this only says it.
 */
import { Badge, Tooltip } from '@mantine/core';
import type { components } from '../api/schema';
import { useVocab } from '../vocab';

type ResolvedDays = components['schemas']['ActiveDaysResolution'];

const NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const nameOf = (d: number) => NAMES[d] as string;

export const DAY_OPTIONS = NAMES.map((label, i) => ({ value: String(i), label }));

export function sortedDays(days: number[]): number[] {
  return [...new Set(days)].sort((a, b) => a - b);
}

export function sameDays(a: number[], b: number[]): boolean {
  return JSON.stringify(sortedDays(a)) === JSON.stringify(sortedDays(b));
}

/** No list, or all seven, is no restriction. */
export function isEveryDay(days: number[]): boolean {
  return days.length === 0 || NAMES.every((_, i) => days.includes(i));
}

function words(days: number[], name: (d: number) => string): string {
  const known = sortedDays(days).filter((d) => d >= 0 && d < NAMES.length);
  // Held on an old row, as nothing here can now store one: the engine matches
  // no day at all, so this must not read as "every day".
  if (known.length === 0) return 'No day';
  // Runs of consecutive days.
  const runs: number[][] = [];
  for (const d of known) {
    const run = runs.at(-1);
    if (run && run.at(-1) === d - 1) run.push(d);
    else runs.push([d]);
  }
  // Three or more in a row read as a range.
  const parts = runs.flatMap((run) =>
    run.length >= 3
      ? [`${name(run[0] as number)} to ${name(run.at(-1) as number)}`]
      : run.map(name),
  );
  const last = parts.pop() as string;
  return parts.length === 0 ? last : `${parts.join(', ')} and ${last}`;
}

/**
 * The day box's placeholder. "Every day" only over an empty box: Mantine shows a
 * placeholder beside the chosen days, which would read "Mon Tue Every day". A
 * space rather than nothing, as with none it drops the input from the page.
 */
export function daysPlaceholder(days: unknown[]): string {
  return days.length === 0 ? 'Every day' : ' ';
}

/** "Monday to Friday", "Monday, Wednesday and Friday", "Every day". */
export function daysWords(days: number[]): string {
  return isEveryDay(days) ? 'Every day' : words(days, nameOf);
}

/** "Mon to Fri", for a table. */
export function daysShort(days: number[]): string {
  return isEveryDay(days) ? 'Every day' : words(days, (d) => nameOf(d).slice(0, 3));
}

/**
 * The route's days, when they matter: a restriction, or a route set to every
 * day against an endpoint that is not. Nothing at all for a route that simply
 * runs every day, so a table stays quiet unless there is something to see.
 */
export function DaysBadge({ days }: { days: ResolvedDays | undefined }) {
  const v = useVocab();
  // The web app goes live before the server: for those minutes there is no
  // `days` to read, and a badge is not worth a blank routing screen.
  if (!days) return null;
  const everyDay = isEveryDay(days.value);
  if (everyDay && !(days.source === 'link' && !isEveryDay(days.destination_value))) return null;
  const whose =
    days.source === 'destination' ? `from the ${v.one('destination')}` : `set on this ${v.one('route')}`;
  const label = everyDay
    ? `Every day, set on this ${v.one('route')}, even though the ${v.one('destination')} is ${daysWords(days.destination_value)}.`
    : `${daysWords(days.value)}, ${whose}.`;
  return (
    <Tooltip label={`${label} Evaluated in UK time.`} multiline w={260}>
      <Badge size="sm" variant="outline" color="gray" tt="none" style={{ flexShrink: 0 }}>
        {daysShort(days.value)}
      </Badge>
    </Tooltip>
  );
}
